import { ArrowRight, Clock, Database } from 'lucide-react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTableColumns } from '../../hooks/useDataCatalog.js';
import { formatBytes, formatNumber, toJstDateTime } from '../../lib/data-catalog-format.js';
import type {
  CatalogDataset,
  CatalogTable,
  FeatureMapping,
  FirestoreCollectionMeta,
  SelectedNode,
} from '../../types/data-catalog.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business データカタログの詳細ペイン。選択されたノードの詳細情報を表示する。
 * @graph-connects useTableColumns [calls] テーブルカラム取得フック
 */
export function DetailPane({
  selectedNode,
  datasets,
  collections,
  featureMapping,
}: {
  selectedNode: SelectedNode;
  datasets: CatalogDataset[];
  collections: FirestoreCollectionMeta[];
  featureMapping: FeatureMapping[];
}) {
  if (!selectedNode) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--color-text-muted)]">
        ← ツリーからリソースを選択
      </div>
    );
  }

  if (selectedNode.type === 'bq-table') {
    const dataset = datasets.find((d) => d.datasetId === selectedNode.datasetId);
    const table = dataset?.tables.find((t) => t.tableId === selectedNode.tableId);
    if (!table || !dataset) return null;
    return <BqTableDetail table={table} dataset={dataset} featureMapping={featureMapping} />;
  }

  if (selectedNode.type === 'bq-dataset') {
    const dataset = datasets.find((d) => d.datasetId === selectedNode.datasetId);
    if (!dataset) return null;
    return <BqDatasetDetail dataset={dataset} />;
  }

  if (selectedNode.type === 'fs-collection') {
    const collection = collections.find((c) => c.shortName === selectedNode.shortName);
    if (!collection) return null;
    return <FsCollectionDetail collection={collection} featureMapping={featureMapping} />;
  }

  return null;
}

/** @graph-connects none */
function BqTableDetail({
  table,
  dataset,
  featureMapping,
}: {
  table: CatalogTable;
  dataset: CatalogDataset;
  featureMapping: FeatureMapping[];
}) {
  const columnsQuery = useTableColumns(dataset.datasetId, table.tableId);
  const relatedFeatures = featureMapping.filter((fm) =>
    fm.bqTables.includes(`${dataset.datasetId}.${table.tableId}`),
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-mono text-base font-bold text-[var(--color-text-primary)]">
          {table.tableId}
        </h3>
        <p className="text-xs text-[var(--color-text-muted)]">{dataset.datasetId}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCard label="種別" value={table.tableType} />
        <MetricCard label="行数" value={formatNumber(table.rowCount)} />
        <MetricCard label="サイズ" value={formatBytes(table.sizeBytes)} />
        <MetricCard label="最終更新" value={toJstDateTime(table.lastModifiedAt)} />
      </div>

      {table.lineage && (
        <Card className="p-3">
          <h4 className="mb-2 text-xs font-semibold text-[var(--color-text-secondary)]">
            データリネージ
          </h4>
          {table.lineage.referencedTables.length > 0 && (
            <div className="space-y-1">
              {table.lineage.referencedTables.map((ref) => (
                <div key={ref} className="flex items-center gap-2 text-xs">
                  <Database className="h-3 w-3 text-[var(--color-text-muted)]" />
                  <span className="font-mono text-[var(--color-text-primary)]">{ref}</span>
                  <ArrowRight className="h-3 w-3 text-[var(--color-text-muted)]" />
                  <span className="font-mono text-[var(--color-accent-primary)]">
                    {table.tableId}
                  </span>
                </div>
              ))}
            </div>
          )}
          {table.lineage.filterCondition && (
            <div className="mt-2 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              フィルタ:
              <code className="rounded bg-[var(--color-bg-base)] px-1.5 py-0.5 font-mono text-[var(--color-text-primary)]">
                {table.lineage.filterCondition}
              </code>
            </div>
          )}
        </Card>
      )}

      {relatedFeatures.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-[var(--color-text-secondary)]">
            使用する機能
          </h4>
          <div className="flex flex-wrap gap-1">
            {relatedFeatures.map((fm) => (
              <span
                key={fm.featureKey}
                className="rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-accent-primary)]"
              >
                {fm.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {columnsQuery.isLoading && (
        <p className="text-xs text-[var(--color-text-muted)]">カラム読み込み中...</p>
      )}
      {columnsQuery.isError && (
        <p className="text-xs text-[var(--color-error)]">カラム情報の取得に失敗しました</p>
      )}
      {columnsQuery.data && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-[var(--color-text-secondary)]">
            カラム ({columnsQuery.data.columns.length})
          </h4>
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-muted)]">
                  <th className="px-3 py-1.5 font-medium">#</th>
                  <th className="px-3 py-1.5 font-medium">カラム名</th>
                  <th className="px-3 py-1.5 font-medium">型</th>
                  <th className="px-3 py-1.5 font-medium">NULL</th>
                </tr>
              </thead>
              <tbody>
                {columnsQuery.data.columns.map((col) => (
                  <tr
                    key={col.ordinalPosition}
                    className="border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <td className="px-3 py-1 font-mono text-[var(--color-text-muted)]">
                      {col.ordinalPosition}
                    </td>
                    <td className="px-3 py-1 font-mono text-[var(--color-text-primary)]">
                      {col.columnName}
                    </td>
                    <td className="px-3 py-1 font-mono text-[var(--color-text-secondary)]">
                      {col.dataType}
                    </td>
                    <td className="px-3 py-1 text-[var(--color-text-muted)]">
                      {col.isNullable ? 'YES' : 'NO'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function BqDatasetDetail({ dataset }: { dataset: CatalogDataset }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-mono text-base font-bold text-[var(--color-text-primary)]">
          {dataset.datasetId}
        </h3>
        <p className="text-xs text-[var(--color-text-muted)]">
          {dataset.layer === 'raw' ? 'raw層' : 'mart層'}
          {dataset.sourceDb && ` ← ${dataset.sourceDb.name} (${dataset.sourceDb.engine})`}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="テーブル数" value={String(dataset.tables.length)} />
        <MetricCard label="合計行数" value={formatNumber(dataset.totalRows)} />
        <MetricCard label="合計サイズ" value={formatBytes(dataset.totalSizeBytes)} />
      </div>

      {dataset.sourceDb && (
        <Card className="p-3">
          <h4 className="mb-2 text-xs font-semibold text-[var(--color-text-secondary)]">
            同期元データベース
          </h4>
          <div className="flex items-center gap-2 text-xs">
            <Database className="h-3 w-3 text-[var(--color-text-muted)]" />
            <span className="font-mono font-medium text-[var(--color-text-primary)]">
              {dataset.sourceDb.name}
            </span>
            <span className="rounded bg-[var(--color-bg-base)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)]">
              {dataset.sourceDb.engine}
            </span>
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {dataset.sourceDb.description}
          </p>
          <div className="mt-2 flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
            <Clock className="h-3 w-3" />
            パイプライン 毎日同期
          </div>
        </Card>
      )}

      <div>
        <h4 className="mb-1 text-xs font-semibold text-[var(--color-text-secondary)]">
          テーブル一覧
        </h4>
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-muted)]">
                <th className="px-3 py-1.5 font-medium">テーブル名</th>
                <th className="px-3 py-1.5 font-medium">種別</th>
                <th className="px-3 py-1.5 font-medium">行数</th>
                <th className="px-3 py-1.5 font-medium">サイズ</th>
              </tr>
            </thead>
            <tbody>
              {dataset.tables.map((t) => (
                <tr
                  key={t.tableId}
                  className="border-b border-[var(--color-border)] last:border-b-0"
                >
                  <td className="px-3 py-1 font-mono text-[var(--color-text-primary)]">
                    {t.tableId}
                  </td>
                  <td className="px-3 py-1">
                    <span
                      className={`rounded-full px-1.5 py-0 text-[9px] font-medium ${
                        t.tableType === 'VIEW'
                          ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                          : 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
                      }`}
                    >
                      {t.tableType}
                    </span>
                  </td>
                  <td className="px-3 py-1 text-[var(--color-text-secondary)]">
                    {formatNumber(t.rowCount)}
                  </td>
                  <td className="px-3 py-1 text-[var(--color-text-muted)]">
                    {formatBytes(t.sizeBytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** @graph-connects none */
function FsCollectionDetail({
  collection,
  featureMapping,
}: {
  collection: FirestoreCollectionMeta;
  featureMapping: FeatureMapping[];
}) {
  const relatedFeatures = featureMapping.filter((fm) =>
    fm.firestoreCollections.includes(collection.shortName),
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-mono text-base font-bold text-[var(--color-text-primary)]">
          {collection.shortName}
        </h3>
        <p className="text-xs text-[var(--color-text-muted)]">{collection.collectionPath}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MetricCard label="ドキュメント数" value={formatNumber(collection.docCount)} />
        <MetricCard
          label="最終更新"
          value={collection.lastUpdatedAt ? toJstDateTime(collection.lastUpdatedAt) : '—'}
        />
      </div>

      {relatedFeatures.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-[var(--color-text-secondary)]">
            使用する機能
          </h4>
          <div className="flex flex-wrap gap-1">
            {relatedFeatures.map((fm) => (
              <span
                key={fm.featureKey}
                className="rounded-full bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-warning)]"
              >
                {fm.label}
              </span>
            ))}
          </div>
          {relatedFeatures[0]?.pipeline && (
            <div className="mt-2 flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
              <Clock className="h-3 w-3" />
              {relatedFeatures[0].pipeline.name} — {relatedFeatures[0].pipeline.schedule}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[var(--color-bg-base)] px-3 py-2">
      <div className="text-[10px] text-[var(--color-text-muted)]">{label}</div>
      <div className="text-sm font-semibold text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}
